import {
	ResponsiveContainer,
	ComposedChart,
	Area,
	Bar,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
} from 'recharts'
import { useState } from 'react'
import { useDarkMode } from '../../context/DarkModeContext'
import dayjs from 'dayjs'

export default function TrendCombo({ daily = [], totals = {} }) {
	const { isDarkMode } = useDarkMode()
	const [showRevenue, setShowRevenue] = useState(true)
	const [showLoad, setShowLoad] = useState(true)
	const [showRpm, setShowRpm] = useState(true)

	const transformedDaily = daily.map((item, index) => {
		if (
			item.date &&
			(item.date.includes('AM') ||
				item.date.includes('PM') ||
				item.date.includes('~'))
		) {
			return item
		} else {
			const formattedDate = dayjs.utc(item.date).format('DD MMM')
			return {
				...item,
				date: formattedDate,
				originalDate: item.date,
				dayOfWeek: item.dayOfWeek,
			}
		}
	})

	const gridColor = isDarkMode ? '#444c56' : '#cccccc'
	const textColor = isDarkMode ? '#adbac7' : '#000000'
	const backgroundColor = isDarkMode ? '#2d333b' : '#ffffff'
	const revenueColor = '#4F8EF7'
	const loadColor = isDarkMode ? '#9ca3af' : '#d1d5db'

	const formatXAxis = tickItem => {
		// For 24h period, show just the start time (e.g., "12 AM-2 AM" → "12 AM")
		if (tickItem && (tickItem.includes('AM') || tickItem.includes('PM'))) {
			// Extract just the start time from "12 AM-2 AM" format
			return tickItem.split('-')[0].trim()
		}
		// For old format "00~02", convert to readable time
		if (tickItem && tickItem.includes('~')) {
			const start = parseInt(tickItem.split('~')[0])
			if (start === 0) return '12 AM'
			if (start === 12) return '12 PM'
			if (start < 12) return `${start} AM`
			return `${start - 12} PM`
		}
		// For 7d+ period, date strings like "22 Oct" - return as-is
		return tickItem
	}

	const formatNumber = (value, prefix = '') => {
		if (value === null || value === undefined) return '0'

		if (value >= 1000000) {
			return `${prefix}${(value / 1000000).toFixed(1)}M`
		}
		if (value >= 1000) {
			return `${prefix}${(value / 1000).toFixed(1)}K`
		}
		return `${prefix}${value.toFixed(1)}`
	}

	return (
		<div
			className="rounded-lg p-4"
			style={{
				backgroundColor,
				boxShadow: isDarkMode
					? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)'
					: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
				border: `1px solid ${isDarkMode ? '#444c56' : '#cccccc'}`,
			}}
		>
			<div className="mb-4 flex flex-wrap items-start gap-8">
				<div className="flex flex-col">
					<label
						className={`inline-flex items-center gap-2 cursor-pointer ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						<input
							type="checkbox"
							className="form-checkbox"
							style={{ color: revenueColor }}
							checked={showRevenue}
							onChange={e => setShowRevenue(e.target.checked)}
						/>
						<span>Total Revenue</span>
					</label>
					<span
						className={`text-2xl font-semibold ${
							isDarkMode ? 'text-white' : 'text-gray-900'
						}`}
					>
						{formatNumber(totals.revenue, '$')}
					</span>
				</div>

				<div className="flex flex-col">
					<label
						className={`inline-flex items-center gap-2 cursor-pointer ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						<input
							type="checkbox"
							className="form-checkbox"
							style={{ color: loadColor }}
							checked={showLoad}
							onChange={e => setShowLoad(e.target.checked)}
						/>
						<span>Total Loads</span>
					</label>
					<span
						className={`text-2xl font-semibold ${
							isDarkMode ? 'text-white' : 'text-gray-900'
						}`}
					>
						{formatNumber(totals.loadCount)}
					</span>
				</div>

				<div className="flex flex-col">
					<label
						className={`inline-flex items-center gap-2 cursor-pointer ${
							isDarkMode ? 'text-gray-300' : 'text-gray-600'
						}`}
					>
						<input
							type="checkbox"
							className="form-checkbox"
							style={{ color: '#10B981' }}
							checked={showRpm}
							onChange={e => setShowRpm(e.target.checked)}
						/>
						<span
							className={`text-sm ${
								isDarkMode ? 'text-gray-400' : 'text-gray-500'
							}`}
						>
							Rate Per Mile (Average)
						</span>
					</label>
					<span
						className={`text-2xl font-semibold ${
							isDarkMode ? 'text-white' : 'text-gray-900'
						}`}
					>
						${totals.avgRpm?.toFixed(2) || '0.00'}
					</span>
				</div>
			</div>

			<ResponsiveContainer width="100%" height={300}>
				<ComposedChart
					data={transformedDaily}
					margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
				>
					<defs>
						<linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor={revenueColor} stopOpacity={0.1} />
							<stop offset="95%" stopColor={revenueColor} stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid
						strokeDasharray="3 3"
						stroke={gridColor}
						vertical={false}
					/>
					<XAxis
						type="category"
						dataKey="date"
						stroke={gridColor}
						tick={{ fill: textColor }}
						tickLine={{ stroke: gridColor }}
						tickFormatter={formatXAxis}
						interval={0}
					/>
					<YAxis
						yAxisId="revenue"
						orientation="left"
						tickFormatter={v => formatNumber(v, '$')}
						stroke={gridColor}
						tick={{ fill: textColor }}
						tickLine={{ stroke: gridColor }}
					/>
					<YAxis
						yAxisId="loads"
						orientation="right"
						tickFormatter={v => formatNumber(v)}
						stroke={gridColor}
						tick={{ fill: textColor }}
						tickLine={{ stroke: gridColor }}
					/>
					{/* Hidden axis to give RPM its own scale */}
					<YAxis yAxisId="rpm" hide domain={[0, 'dataMax']} />
					<Tooltip
						contentStyle={{
							backgroundColor: isDarkMode ? '#2d333b' : '#ffffff',
							border: 'none',
							borderRadius: '12px',
							boxShadow:
								'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
							color: textColor,
							padding: '16px',
						}}
						labelStyle={{
							color: isDarkMode ? '#adbac7' : '#6b7280',
							fontSize: '14px',
							fontWeight: '500',
							marginBottom: '8px',
							padding: '0',
						}}
						itemStyle={{
							color: isDarkMode ? '#ffffff' : '#111827',
							fontSize: '13px',
							padding: '4px 0',
						}}
						labelFormatter={(label, payload) => {
							// For time blocks (new format: "12 AM-2 AM" or old format: "00~02")
							if (label && (label.includes('AM') || label.includes('PM'))) {
								// New format already readable, just return as-is
								return label.replace('-', ' - ')
							}
							if (label && label.includes('~')) {
								// Old format - convert to readable time
								const [start, end] = label.split('~').map(Number)
								const formatHour = h => {
									if (h === 0) return '12:00 AM'
									if (h === 12) return '12:00 PM'
									if (h < 12) return `${h}:00 AM`
									return `${h - 12}:00 PM`
								}
								return `${formatHour(start)} - ${formatHour(end)}`
							}
							// For dates, use dayOfWeek from backend to show correct day
							if (payload && payload[0] && payload[0].payload) {
								const { originalDate, dayOfWeek } = payload[0].payload
								if (dayOfWeek && originalDate) {
									// Format: "Wednesday, 29 October"
									return `${dayOfWeek}, ${dayjs
										.utc(originalDate)
										.format('DD MMMM')}`
								}
							}
							// Fallback to parsing date (UTC to maintain consistency)
							return dayjs.utc(label).format('dddd, DD MMMM')
						}}
						formatter={(value, name) => {
							switch (name) {
								case 'Revenue':
									return ['Total Revenue', formatNumber(value, '$')]
								case 'Loads':
									return ['Total Loads', formatNumber(value)]
								case 'RPM':
									return ['Rate Per Mile (Average)', `$${value.toFixed(2)}`]
								default:
									return [name, formatNumber(value)]
							}
						}}
						wrapperStyle={{
							outline: 'none',
						}}
					/>
					{showLoad && (
						<Bar
							yAxisId="loads"
							dataKey="loadCount"
							name="Loads"
							fill={loadColor}
							radius={[4, 4, 0, 0]}
							fillOpacity={0.9}
						/>
					)}
					{showRevenue && (
						<Area
							yAxisId="revenue"
							type="monotone"
							dataKey="revenue"
							name="Revenue"
							fill="url(#revenueGradient)"
							stroke={revenueColor}
							strokeWidth={2}
						/>
					)}
					{showRpm && (
						<Line
							yAxisId="rpm"
							type="monotone"
							dataKey="rpm"
							name="RPM"
							stroke="#10B981"
							strokeWidth={2}
							dot={false}
						/>
					)}
				</ComposedChart>
			</ResponsiveContainer>
		</div>
	)
}
